import { ProductCategory } from '~/utils/claimUtils';
import { OrderItemProductType, ProductTypeEnum } from './ordersTypes';
import { IconName } from '~/components/core/Icon';
import { ColorModeEnum } from '~/utils/generalUtils';

export type RelatedLink = {
  href: string;
  templated: boolean;
};

export type RelatedLinks = {
  [key: string]: RelatedLink;
};

export type ParamValueType = {
  value?: string;
  numeric_value?: number;
  parameter?: string;
  parameterId?: string;
  unitSymbol?: string | null;
  unitSymbolRequired?: boolean;
  _links: RelatedLinks;
};

export type ProductPriceType = {
  id: string;
  version: string;
  aggregation: {
    total_quantity: number;
    available_quantity: number;
    earliest_delivery_date: string; // ISO date string
    latest_delivery_date: string; // ISO date string
    earliest_expiration_date: string; // ISO datetime string
    latest_expiration_date: string; // ISO datetime string
    selected_pq_price_expiration_date: string;
  };
  quantity: number;
  currency: string;
  selected_pqs: Record<string, any>[]; // Adjust based on actual object structure
  purchase_price: number;
  base_price: number;
  sales_price: number;
  margin_rate: number;
  item_total: number;
  subtotal: number;
  logistics_cost: {
    harbour: number;
    freight: number;
    warehouse: number;
    last_mile: number;
    pq: number;
    total: number;
  };
  delivery_cost: number; // Includes last mile, warehouse, vessel, harbour and freight costs
  delivery_cost_discounted?: boolean; // Ex value: true
  shipping_fee: number; // Includes only vessel, harbour and freight costs
  shipping_fee_discounted: boolean; // Ex value: 0.00 -> false, 1.03 -> true
  feedback: {
    AGGREGATED_VALUES: string;
    BASE_PRICE: string;
    FLOOR_PRICE: string;
    PURCHASE_PRICE: string;
    ERR_NOT_ENOUGH_AVAILABLE_QUANTITY?: string;
    ERR_NO_PQ_FOUND?: string;
    ERR_NO_MARGIN_FOUND?: string;
  };
};

export type KeyFeatureParameter =
  | 'Warranty'
  | 'Inverter Type'
  | 'Module Power'
  | 'Module Type'
  | 'Primary (Years, % Output Power)'
  | 'Panel Efficiency (%) At STC'
  | 'Cell Type'
  | 'Length'
  | 'Cable colour'
  | 'Colour'
  | 'Cable type'
  | 'Number of phases'
  | 'Inverter Power'
  | 'Capacity';

export type ParamValueTypeForKeyProperties = Omit<ParamValueType, 'parameter'> & {
  parameter: KeyFeatureParameter;
};

export type ProductType = {
  status?: string;
  id: string;
  salesforce_id: string;
  name: string;
  brand?: string;
  product_image?: ParamValueType;
  price: ProductPriceType;
  flag?: string;
  category: {
    id: ProductCategory | string;
    name: string;
  };
  keyFeatures?: ParamValueTypeForKeyProperties[];
  documents?: ParamValueType[];
  details?: ParamValueType[];
  measurements?: ParamValueType[];
  warrantyDetails?: ParamValueType[];
  warranty_details?: ParamValueType[];
  description?: ParamValueType;
  parameterValues?: ParamValueType[];
  deliveryDetails?: ParamValueType[];
  recommended?: boolean;
  main_parameters?: ParamValueType[];
  grouped_documents?: Record<string, ParamValueType[]>;
};

/**
 * Returns product category names as used in the Backend
 * - Modules = 'PV Modules'
 * - ModulesLowCarbon = 'PV Modules - LOw-Carbon'
 * - ModulesCommercialInfrastructure = 'PV Modules - Commercial / Infrastructure'
 * - Batteries = 'PV Batteries'
 * - BatteriesLargeScaleBESS = 'PV Batteries - Large-Scale BESS'
 * - Inverters = 'PV Inverters'
 * - InvertersCommercialInfrastructure = 'PV Inverter - Commercial / Infrastructure'
 * - Optimizer = 'PV Optimizer'
 * - ElectricCables = 'Electric cables'
 * - Wallboxes = 'PV Wallboxes'
 * - MountingSystems = 'PV Mounting systems'
 * - BuildingAutomationIoTDevices = 'Building Automation - IoT devices'
 * - XPS = 'XPS'
 * - Others = 'PV Others'
 */
export enum ProductCategoryBEnum {
  Modules = 'PV Modules',
  ModulesLowCarbon = 'PV Modules - LOw-Carbon',
  ModulesCommercialInfrastructure = 'PV Modules - Commercial / Infrastructure',
  Batteries = 'PV Batteries',
  BatteriesLargeScaleBESS = 'PV Batteries - Large-Scale BESS',
  Inverters = 'PV Inverters',
  InvertersCommercialInfrastructure = 'PV Inverter - Commercial / Infrastructure',
  Optimizer = 'PV Optimizer',
  ElectricCables = 'Electric cables',
  Wallboxes = 'PV Wallboxes',
  MountingSystems = 'PV Mounting systems',
  BuildingAutomationIoTDevices = 'Building Automation - IoT devices',
  XPS = 'XPS',
  Others = 'PV Others',
}

/**
 * Returns product category names as used in the Frontend
 * - Modules = 'modules'
 * - ModulesLowCarbon = 'modules-low-carbon'
 * - ModulesCommercialInfrastructure = 'modules-commercial-infrastructure'
 * - Batteries = 'batteries'
 * - BatteriesLargeScaleBESS = 'batteries-large-scale-bess'
 * - Inverters = 'inverters'
 * - InverterCommercialInfrastructure = 'inverter-commercial-infrastructure'
 * - Optimizer = 'optimizer'
 * - ElectricCables = 'electric-cables'
 * - Wallboxes = 'wallboxes'
 * - MountingSystems = 'mounting-systems'
 * - BuildingAutomationIoTDevices = 'building-automation-iot-devices'
 * - XPS = 'xps'
 * - Others = 'others'
 */
export enum ProductCategoryFEnum {
  Modules = 'modules',
  ModulesLowCarbon = 'modules-low-carbon',
  ModulesCommercialInfrastructure = 'modules-commercial-infrastructure',
  Batteries = 'batteries',
  BatteriesLargeScaleBESS = 'batteries-large-scale-bess',
  Inverters = 'inverters',
  InverterCommercialInfrastructure = 'inverter-commercial-infrastructure',
  Optimizer = 'optimizer',
  ElectricCables = 'electric-cables',
  Wallboxes = 'wallboxes',
  MountingSystems = 'mounting-systems',
  BuildingAutomationIoTDevices = 'building-automation-iot-devices',
  XPS = 'xps',
  Others = 'others',
}

export type ProductCardType = {
  id: string;
  name: string;
  brand?: string;
  price?: number;
  category: {
    id: string;
    name?: ProductCategory | null;
  };
  flag?: string;
  productImage?: { value?: string };
  feedback: {
    ERR_NOT_ENOUGH_AVAILABLE_QUANTITY?: string;
    ERR_NO_MARGIN_FOUND?: string;
    ERR_NO_PQ_FOUND?: string;
  };
  earliest_delivery_date?: string;
  measurements?: ParamValueType[];
};

export type CategoryValuesType = {
  icon: IconName;
  color?: string;
  name: string;
  link: string;
  colorMode?: ColorModeEnum;
  size?: number;
  strokeWidth?: number;
};
/**
 * Returns possible price units
 * - Container = 'container'
 * - Piece = 'piece'
 * - Metre = 'metre'
 * - UnitEnum = 'unit'
 * - Palet = 'pallet'
 */
export enum PriceUnitEnum {
  Container = 'container',
  Piece = 'piece',
  Metre = 'metre',
  UnitEnum = 'unit',
  Pallet = 'pallet',
}

export type CategoryType = {
  name: string;
  parent: {
    href: string;
    templated: boolean;
  } | null;
  id: string;
  children: [];
  _links: {
    [key: string]: string;
  };
};

export type SupportedCategoriesType = {
  [ProductCategoryFEnum.Batteries]: {
    id: string;
    value: string;
    label: string;
  };
  [ProductCategoryFEnum.Modules]: { id: string; value: string; label: string };
  [ProductCategoryFEnum.Inverters]: {
    id: string;
    value: string;
    label: string;
  };
};

export type SupportedCategoryType = {
  value: string | ProductCategoryFEnum;
  id: string;
};

export type ValueMappingType = { [key: string]: string };

/**
 * Returns possible store access status
 * used to determine if the user has visited the coming soon page already and if he has activated the notification
 * - FirstVisited = 'firstVisited'
 * - ActivatedNotification = 'activatedNotification'
 * - VisitedAgain = 'visitedAgain'
 */
export enum StoreAccessStatusEnum {
  FirstVisited = 'firstVisited',
  ActivatedNotification = 'activatedNotification',
  VisitedAgain = 'visitedAgain',
}

export type CheckoutDetailsType = {
  id: string;
  sf_id: string;
  andercore_id: string;
  subtotal: number;
  bnpl_credit_options: BNPLCreditOptionsType[];
  standard_payment_options: StandardPaymentOptionsType[];
  delivery_cost: number; // This comes from quote endpoint, so remains the same. (NOT changing to total_shipping_fee)
  delivery_cost_discounted?: boolean;
};

export type StandardPaymentOptionsType = {
  payment_option_key: string;
  prepayment_percentage: number;
  prepayment_amount: number;
  after_delivery_percentage: number;
  after_delivery_amount: number;
  credit_period_in_days: number;
};

export type BNPLCreditOptionsType = {
  payment_option_key: string;
  credit_period_in_days: number;
  payment_due_window: string;
  is_best_value: boolean;
  is_most_popular: boolean;
  credit_option_fee: number;
  credit_option_percentage: number;
  showPaywall?: boolean;
};

export enum BNPLResponseStatusesEnum {
  CREATED = 'CREATED',
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  CONFIRMED = 'CONFIRMED',
  DECLINED = 'DECLINED',
  CANCELED = 'CANCELED',
  COMPLETED = 'COMPLETED',
  UNKNOWN = 'UNKNOWN',
}
export type ExecutiveAccountType = {
  ownerId: string;
  andercoreId: string | number;
};

export type ShoppingCartOrderType = {
  sf_id: string;
  quote_number: string;
  account_name?: string;
  shipping_address: {
    street: string;
    city: string;
    country: string;
    postal_code: string;
    email?: string;
  };
  category: string;
  items: OrderItemProductType[];
  owner_id: string;
  andercore_id: string;
  shipping_fee: number;
  delivery_cost: number;
  delivery_cost_discounted: boolean;
  total_cost: number;
  total_price: number;
  isAddressValid: boolean;
  validationObject: object;
  items_merged?: boolean;
  core_financing?: number;
};

export type ProductDetailsDataType = {
  product_id: string | undefined;
  selected_pqs: Record<string, any>[];
  sales_price: number;
  price_engine_id: string;
  delivery_date: string | undefined;
  price_valid_until: string;
  user_selected_unit: ProductTypeEnum | undefined;
  product_key: string | undefined;
};

export type PriceEngineDataType = {
  product_key: string;
  product_id: string;
  reference_id: string;
  delivery: {
    destination: {
      postcode: string;
      country: string;
    };
    quantity: number;
    delivery_date: string;
  };
}[];

type PriceInfo = Record<string, ProductPriceType>;

export type PriceEngineResponseType = {
  price_info: PriceInfo;
  shipping_fee: number;
  shipping_fee_discounted: boolean;
  total_cost: number;
  total_goods_price: number;
};

export type ProductPresentFieldsType = {
  present_fields: string[];
  sf_id: string;
  sf_created_at: string;
  name: string;
  image_url: string;
  size: string;
  color: string | null;
  category: string;
  subcategory: string;
  unit: string;
  brand: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  is_adr: boolean;
  model: string;
  units_per_pallet: number;
  units_per_container: number;
  can_only_be_sold_in_multiples_of: number;
  record_type_id: string;
};

export type CartItemType = {
  id: string;
  cart_id: string;
  delivery_date: string;
  user_selected_unit: ProductTypeEnum;
  pq_id: string;
  price_engine_id: string;
  price_valid_until: string;
  product_id: string;
  total_sales_price: number;
  quantity: number;
  sales_price: number;
  unit: string | null;
  product: ProductPresentFieldsType;
  product_key: string;
  price_info: ProductPriceType;
};

export type CartType = {
  id: string;
  andercore_id: string | null;
  delivery_period: string | null;
  expiration_date: string | null;
  name: string;
  payment_terms: string | null;
  payment_terms_locale: Record<string, string>;
  pdf_link: string | null;
  placed_date: string | null;
  shipping_address_validated: boolean;
  shipping_city: string;
  shipping_code: string;
  shipping_country: string;
  shipping_street: string;
  shipping_name: string;
  status: string;
  total_price: number;
  delivery_contact_name: string;
  delivery_contact_email: string;
  delivery_contact_phone: string;
  warehouse_opening_hours: string;
  maximum_delivery_capacity: string;
  accept_container_delivery: boolean;
  forklift_available: boolean;
  delivery_additional_info: string | null;
  payment_term_days: number;
  payment_method: string;
  account_id: string;
  contact_id: string;
  owner_id: string;
  items_merged: boolean;
  total_shipping_fee: number;
  total_shipping_fee_discounted: number;
  total_cost: number;
  total_goods_price: number;
};

export type CartDetailsType = {
  cart: CartType;
  cart_items: CartItemType[];
};

export type AvailablePQ = {
  pq_id: string;
  available_quantity: number;
  sales_price: number;
  valid_until: string; // ISO date string
};

export type WeeklyPrice = {
  start_date: string; // ISO date string
  available_pqs: AvailablePQ[];
};
